// components/bids/documents/ExtractionProgressModal.jsx

import React, { useState, useEffect } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';

const STEPS = [
  { label: 'Fetching PDF from storage',    duration: 1500  },
  { label: 'Extracting text from PDF',     duration: 3500  },
  { label: 'Sending to Gemini AI',         duration: 10000 },
  { label: 'Parsing structured response',  duration: 1500  },
  { label: 'Calculating confidence score', duration: 500   },
];

const ExtractionProgressModal = ({
  documentName,
  isVisible,
  isComplete,
  currentDoc = 1,   // 1-based index in the queue
  totalDocs  = 1,   // total docs being processed
}) => {
  const [activeStep, setActiveStep] = useState(0);

  // Reset + start timer chain when modal becomes visible
  useEffect(() => {
    if (!isVisible) { setActiveStep(0); return; }
    setActiveStep(0);
    let current = 0;
    let timeouts = [];
    const scheduleNext = () => {
      if (current >= STEPS.length - 1) return;
      const t = setTimeout(() => {
        current += 1;
        setActiveStep(current);
        scheduleNext();
      }, STEPS[current].duration);
      timeouts.push(t);
    };
    scheduleNext();
    return () => timeouts.forEach(clearTimeout);
  }, [isVisible]);

  // Snap to done when API completes
  useEffect(() => {
    if (isComplete && isVisible) {
      setActiveStep(STEPS.length - 1);
      const t = setTimeout(() => setActiveStep(STEPS.length), 500);
      return () => clearTimeout(t);
    }
  }, [isComplete, isVisible]);

  if (!isVisible) return null;

  const doneCount   = Math.min(activeStep, STEPS.length);
  const progressPct = Math.round((doneCount / STEPS.length) * 100);
  const showQueue   = totalDocs > 1;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8">

        {/* Icon + title */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 bg-brand-50 rounded-full flex items-center justify-center mb-4 relative">
            <SparklesIcon className="w-7 h-7 text-brand-500 animate-pulse" />
            {showQueue && (
              <span className="absolute -top-1 -right-1 w-6 h-6 bg-darkblue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {currentDoc}/{totalDocs}
              </span>
            )}
          </div>
          <h3 className="text-base font-semibold text-darkblue-500">
            {showQueue ? `Processing Document ${currentDoc} of ${totalDocs}` : 'Processing Document'}
          </h3>
          {documentName && (
            <p className="text-sm text-grey-500 mt-1 truncate max-w-xs text-center">{documentName}</p>
          )}
          {/* Queue progress pills */}
          {showQueue && (
            <div className="flex items-center gap-1.5 mt-3">
              {Array.from({ length: totalDocs }).map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${
                  i < currentDoc - 1 ? 'w-4 bg-teal-500' :
                  i === currentDoc - 1 ? 'w-6 bg-brand-500' :
                  'w-4 bg-grey-200'
                }`} />
              ))}
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-grey-100 rounded-full h-2 mb-6">
          <div
            className="bg-brand-500 h-2 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {STEPS.map((step, i) => {
            const isDone   = i < activeStep;
            const isActive = i === activeStep;
            return (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {isDone ? (
                    <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : isActive ? (
                    <div className="w-5 h-5 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-grey-200" />
                  )}
                </div>
                <span className={`text-sm ${
                  isDone ? 'text-teal-700 font-medium' :
                  isActive ? 'text-brand-600 font-medium' :
                  'text-grey-400'
                }`}>{step.label}</span>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-grey-400 text-center mt-6">
          {showQueue
            ? `${totalDocs - currentDoc} document${totalDocs - currentDoc !== 1 ? 's' : ''} remaining after this`
            : 'This usually takes 10–15 seconds'
          }
        </p>
      </div>
    </div>
  );
};

export default ExtractionProgressModal;